import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TitleCasePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ModuleService } from '../../../services/module.service';
import { ToastService } from '../../../services/toast.service';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { Module } from '../../../models/module.model';
import { MaterialModule } from '../../../shared/material.module';
import { ModuleDialogComponent, DialogMode } from '../module-dialog/module-dialog.component';
import { PaginationComponent, PaginationEvent } from '../../../shared/components/pagination/pagination.component';
import { ModuleStatus } from '../../../models/module.model';

@Component({
  selector: 'app-module-list',
  standalone: true,
  imports: [FormsModule, MaterialModule, TitleCasePipe, PaginationComponent],
  templateUrl: './module-list.component.html',
  styleUrl: './module-list.component.scss'
})
export class ModuleListComponent implements OnInit {
  modules: Module[] = [];
  filteredModules: Module[] = [];
  paginatedModules: Module[] = [];
  searchTerm = '';
  selectedType = '';
  moduleTypes: string[] = [];
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  pageSizeOptions = [5, 10, 20, 50];

  constructor(
    private moduleService: ModuleService,
    private toastService: ToastService,
    private errorHandler: ErrorHandlerService,
    private confirmationService: ConfirmationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadModules();
  }

  loadModules(): void {
    this.moduleService.getAllModules().subscribe({
      next: (modules: Module[]) => {
        this.modules = modules;
        this.filteredModules = modules;
        this.totalItems = modules.length;
        this.extractModuleTypes();
        this.updatePaginatedModules();
      },
      error: (error: any) => {
        const errorResult = this.errorHandler.handleError(error);
        this.toastService.error('Error', errorResult.message);
        
        // Load mock data as fallback for demonstration
        this.loadMockData();
      }
    });
  }

  loadMockData(): void {
    // Mock data for demonstration
    this.modules = [
      {
        id: '1',
        name: 'Space Management',
        slug: 'space-management',
        description: 'Comprehensive space and location management with hierarchical structure, asset tracking, and occupancy monitoring.',
        icon: 'business',
        color: '#0c2864',
        version: '1.0.0',
        features: ['Location Hierarchy', 'Asset Tracking', 'Occupancy Management', 'Space Utilization'],
        permissions: {
          read: ['view_locations', 'view_assets'],
          write: ['create_location', 'edit_location', 'assign_assets'],
          admin: ['delete_location', 'manage_permissions']
        },
        sortOrder: 1,
        isCore: true,
        type: 'Business',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-01-15'),
        updatedAt: new Date('2024-01-15')
      },
      {
        id: '2',
        name: 'Documents',
        slug: 'documents',
        description: 'Document management, storage, and collaboration with version control and access permissions.',
        icon: 'description',
        color: '#059669',
        version: '1.2.0',
        features: ['Document Storage', 'Version Control', 'Collaboration', 'Access Control'],
        permissions: {
          read: ['view_documents'],
          write: ['upload_document', 'edit_document'],
          admin: ['delete_document', 'manage_sharing']
        },
        sortOrder: 2,
        isCore: false,
        type: 'Business',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-02-01'),
        updatedAt: new Date('2024-02-01')
      },
      {
        id: '3',
        name: 'Energy Management',
        slug: 'energy-management',
        description: 'Monitor and optimize energy consumption and efficiency across all facilities and equipment.',
        icon: 'bolt',
        color: '#d97706',
        version: '1.1.0',
        features: ['Energy Monitoring', 'Efficiency Analytics', 'Cost Management', 'Sustainability Reports'],
        permissions: {
          read: ['view_energy_data'],
          write: ['edit_energy_settings'],
          admin: ['manage_energy_system']
        },
        sortOrder: 3,
        isCore: false,
        type: 'Specialized',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-03-15'),
        updatedAt: new Date('2024-03-15')
      },
      {
        id: '4',
        name: 'Reports',
        slug: 'reports',
        description: 'Generate and manage business reports and analytics with customizable dashboards.',
        icon: 'assessment',
        color: '#7c3aed',
        version: '1.0.5',
        features: ['Custom Reports', 'Analytics Dashboard', 'Data Export', 'Scheduled Reports'],
        permissions: {
          read: ['view_reports'],
          write: ['create_report'],
          admin: ['manage_report_templates']
        },
        sortOrder: 4,
        isCore: true,
        type: 'Business',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-04-01'),
        updatedAt: new Date('2024-04-01')
      },
      {
        id: '5',
        name: 'Workflow',
        slug: 'workflow',
        description: 'Business process automation and workflow management with approval chains.',
        icon: 'device_hub',
        color: '#dc2626',
        version: '1.3.0',
        features: ['Process Automation', 'Approval Workflows', 'Task Management', 'Integration APIs'],
        permissions: {
          read: ['view_workflows'],
          write: ['create_workflow', 'edit_workflow'],
          admin: ['delete_workflow', 'manage_workflow_permissions']
        },
        sortOrder: 5,
        isCore: false,
        type: 'Business',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-05-10'),
        updatedAt: new Date('2024-05-10')
      },
      {
        id: '6',
        name: 'Projects',
        slug: 'projects',
        description: 'Project management, planning, and execution tracking with resource allocation.',
        icon: 'work',
        color: '#0891b2',
        version: '1.1.2',
        features: ['Project Planning', 'Resource Management', 'Progress Tracking', 'Gantt Charts'],
        permissions: {
          read: ['view_projects'],
          write: ['create_project', 'edit_project'],
          admin: ['delete_project', 'manage_project_access']
        },
        sortOrder: 6,
        isCore: false,
        type: 'Business',
        status: ModuleStatus.ACTIVE,
        createdAt: new Date('2023-06-20'),
        updatedAt: new Date('2024-06-20')
      }
    ];

    this.filteredModules = this.modules;
    this.totalItems = this.modules.length;
    this.extractModuleTypes();
    this.updatePaginatedModules();
  }

  extractModuleTypes(): void {
    const types = [...new Set(this.modules.map(module => module.type).filter((type): type is string => !!type))];
    this.moduleTypes = types.sort();
  }

  filterModules(): void {
    let filtered = this.modules;

    // Filter by search term
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(module =>
        module.name.toLowerCase().includes(term) ||
        module.slug.toLowerCase().includes(term) ||
        (module.description && module.description.toLowerCase().includes(term))
      );
    }

    // Filter by type
    if (this.selectedType) {
      filtered = filtered.filter(module => module.type === this.selectedType);
    }

    this.filteredModules = filtered;
    this.totalItems = filtered.length;
    this.currentPage = 1; // Reset to first page when filtering
    this.updatePaginatedModules();
  }

  updatePaginatedModules(): void {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedModules = this.filteredModules.slice(startIndex, endIndex);
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage = event.pageIndex + 1; // Convert from 0-based to 1-based
    this.pageSize = event.pageSize;
    this.updatePaginatedModules();
  }

  onTypeChange(): void {
    this.filterModules();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filterModules();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedType = '';
    this.filterModules();
  }

  getActiveModuleCount(): number {
    return this.filteredModules.filter(m => m.status === ModuleStatus.ACTIVE).length;
  }

  getCoreModuleCount(): number {
    return this.filteredModules.filter(m => m.isCore).length;
  }

  getModuleTypeClass(type: string | undefined): string {
    if (!type) return 'type-other';
    const typeClass = type.toLowerCase().replace(/\s+/g, '-');
    return `type-${typeClass}`;
  }

  getModuleGradient(color: string): string {
    return `linear-gradient(135deg, ${color}, ${this.lightenColor(color, 20)})`;
  }

  private lightenColor(color: string, percent: number): string {
    // Simple color lightening - parse hex color and increase RGB values
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1);
  }

      openCreateDialog(): void {
        const dialogRef = this.dialog.open(ModuleDialogComponent, {
          width: '900px',
          maxWidth: '90vw',
          disableClose: true,
          data: { mode: DialogMode.CREATE }
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadModules(); // Reload the list
      }
    });
  }

      openEditDialog(module: Module): void {
        const dialogRef = this.dialog.open(ModuleDialogComponent, {
          width: '900px',
          maxWidth: '90vw',
          disableClose: true,
          data: { mode: DialogMode.EDIT, module: module }
        });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadModules(); // Reload the list
      }
    });
  }

      openViewDialog(module: Module): void {
        const dialogRef = this.dialog.open(ModuleDialogComponent, {
          width: '900px',
          maxWidth: '90vw',
          disableClose: true,
          data: { mode: DialogMode.VIEW, module: module }
        });
  }

  deleteModule(module: Module): void {
    this.confirmationService.confirmDelete(module.name, 'module').subscribe({
      next: (confirmed) => {
        if (confirmed) {
          this.moduleService.deleteModule(module.id).subscribe({
            next: () => {
              this.modules = this.modules.filter(m => m.id !== module.id);
              this.filteredModules = this.filteredModules.filter(m => m.id !== module.id);
              this.updatePaginatedModules();
              this.toastService.success('Module Deleted', 'Module has been deleted successfully');
            },
            error: (error: any) => {
              const errorResult = this.errorHandler.handleError(error);
              this.toastService.error('Error', errorResult.message);
            }
          });
        }
      }
    });
  }
}